import { flexRender, Table } from '@tanstack/react-table';
import { FC, Fragment, useRef } from 'react';
import { classNames } from 'primereact/utils';
import { DataTableProgressBarRow, UploadIcon } from '@repo/atomic-components';
import { useTableSpacerWidth } from '@/hooks';
import { UploadDocumentsTableRow } from '../hooks';

interface UploadedDocumentsTableProps {
  table: Table<UploadDocumentsTableRow>;
}

// Mirrors apps/.../MedicalRecordsPage/components/MedicalRecordsListTable.tsx
// so the case-page upload table matches the global medical-records view.
// The shared visuals: 14/20 cell font, 60px row, gray-110 row dividers,
// white bg, hover gray-50, sticky header with box-shadow underline,
// spacer cells driven by `useTableSpacerWidth` to keep total table
// width at 100% without firing a horizontal scroll.
export const UploadedDocumentsTable: FC<UploadedDocumentsTableProps> = ({ table }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const rows = table.getRowModel().rows;

  useTableSpacerWidth(containerRef, tableRef, [rows]);

  return (
    <div ref={containerRef} className="h-full w-full">
      <table
        ref={tableRef}
        className={classNames('min-w-full border-none', {
          'h-full': rows.length === 0,
        })}
      >
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="sticky top-0 z-10 h-12 max-h-12 min-h-12">
              {headerGroup.headers.map((header, headerIndex) => (
                <Fragment key={header.id}>
                  {/* Header underline uses an inset `box-shadow` instead
                      of a `border-b` because Chrome / Safari drop the
                      bottom border on sticky `<th>` cells. The colour
                      matches the inter-row dividers (gray-110). */}
                  <th className="bg-fletchers-white px-2 py-1" style={{ boxShadow: 'inset 0 -1px 0 #E1E1E0' }}>
                    <div className="flex items-center justify-start">
                      <div className="text-fletchers-gray-175 flex items-center justify-center text-[14px]/[20px] font-[600]">
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </div>
                    </div>
                  </th>
                  {/* Spacer header cell — skip after select checkbox
                      column (index 0) and the last column. */}
                  {headerIndex !== 0 && headerIndex !== headerGroup.headers.length - 1 && (
                    <th
                      aria-hidden
                      data-spacer
                      className="bg-fletchers-white"
                      style={{ width: 'var(--spacer-w, 100%)', boxShadow: 'inset 0 -1px 0 #E1E1E0' }}
                    />
                  )}
                </Fragment>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {rows.length ? (
            rows.map((row) => (
              <Fragment key={row.id}>
                <tr
                  key={row.id}
                  className={classNames(
                    'group h-[60px] max-h-[60px] min-h-[60px]',
                    'bg-fletchers-white border-fletchers-gray-110 border-b last:border-b-0',
                    'hover:bg-fletchers-gray-50',
                  )}
                >
                  {row.getVisibleCells().map((cell, cellIndex) => (
                    <Fragment key={cell.id}>
                      <td className={classNames('border-none px-2 py-1', 'text-[14px]/[20px] font-[400]')}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext()) as React.ReactNode}
                      </td>
                      {/* Spacer body cell — skip after select checkbox
                          column (index 0) and the last column. */}
                      {cellIndex !== 0 && cellIndex !== row.getVisibleCells().length - 1 && (
                        <td
                          aria-hidden
                          data-spacer
                          className="border-none"
                          style={{ width: 'var(--spacer-w, 100%)' }}
                        />
                      )}
                    </Fragment>
                  ))}
                </tr>

                {row.original.progressBars && (
                  <DataTableProgressBarRow
                    // Spacers sit between every real column except after the
                    // first and last, so the row renders 2N − 2 cells, not N.
                    colSpan={table.getAllColumns().length * 2 - 2}
                    progressBars={row.original.progressBars}
                  />
                )}
              </Fragment>
            ))
          ) : (
            // Empty-state — mirrors the FileDropOverlay copy so the
            // affordance "you can drop / upload PDFs here" is
            // surfaced even before the user starts dragging.
            <tr>
              <td colSpan={table.getAllColumns().length * 2}>
                <div className="flex h-full flex-col items-center justify-center border-none px-2 py-12">
                  <UploadIcon className="text-fletchers-black-900 mb-6" style={{ width: '32px', height: '32px' }} />
                  <p className="text-fletchers-black-900 mb-2 text-xl font-semibold">Upload medical records</p>
                  <p className="text-fletchers-gray-200 text-sm">Drag and drop PDFs anywhere.</p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
